/**
 * /my-coaching/business/nova/assets/$assetKey/$itemId — one item of a collection (one
 * LinkedIn post, by its lineage id): the asset page scoped to that item.
 *
 * Only a collection with item pages has them, and an item the coach does not
 * own is a 404. A post opened by its row id before it had a lineage redirects
 * to its lineage once it has one. The breadcrumb names the post by its
 * opening line.
 */

import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { AssetPage } from "~/components/nova/assets/asset-page";
import { postRouteTitle } from "~/components/nova/assets/view-model";
import {
  novaAssetCollectionQueryOptions,
  novaAssetDetailQueryOptions,
  novaAssetsQueryOptions,
} from "~/lib/queries/novaAssetsQuery";
import { hasItemPages } from "~/lib/shared/nova/assets/catalog";

export const Route = createFileRoute(
  "/_user/my-coaching/business/nova/assets/$assetKey/$itemId",
)({
  // The loader's opening line replaces this once the item resolves.
  staticData: { title: "Item" },
  beforeLoad: ({ context, params }) => {
    if (
      !hasItemPages(context.asset) ||
      !z.string().uuid().safeParse(params.itemId).success
    ) {
      throw notFound();
    }
  },
  loader: async ({ context, params }) => {
    const { asset } = context;
    const { itemId } = params;
    const userId = context.user.clerkUserId;
    // The list and the collection are prefetched for SSR only, as on the
    // asset page. The detail also decides whether the item exists, so only a
    // read that answered "no such item" is a 404; a failed read renders the
    // page's own error state.
    const [, detail] = await Promise.all([
      context.queryClient
        .ensureQueryData(novaAssetsQueryOptions(userId))
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(novaAssetDetailQueryOptions(userId, asset.key, itemId))
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData(novaAssetCollectionQueryOptions(userId, asset.key))
        .catch(() => undefined),
    ]);
    if (detail === null) throw notFound();
    if (detail && "movedTo" in detail) {
      throw redirect({
        to: "/my-coaching/business/nova/assets/$assetKey/$itemId",
        params: { assetKey: asset.key, itemId: detail.movedTo },
        replace: true,
      });
    }
    return {
      title: postRouteTitle(asset.key, detail),
      assetKey: asset.key,
      itemId,
      userId,
    };
  },
  component: ItemRoute,
});

function ItemRoute() {
  const { assetKey, itemId, title, userId } = Route.useLoaderData();
  return (
    <AssetPage
      assetKey={assetKey}
      itemId={itemId}
      title={title}
      userId={userId}
    />
  );
}
